import type {
  DeliveryMethodBody,
  GiftCardBody,
  PaymentBodyProvider,
  PaymentBodyStatus,
  PromoBody,
  ReturnBodyStatus,
} from '@/shared/api/generated/model';

/** Сумма для людей: «1 290,50 ₽». */
export function money(value: number, currency = 'RUB'): string {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency,
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export const PROVIDER: Record<PaymentBodyProvider, string> = {
  test: 'Тестовая оплата',
  yookassa: 'ЮKassa',
};

export const PAYMENT_STATUS: Record<
  PaymentBodyStatus,
  { label: string; tone: 'warning' | 'success' | 'neutral' | 'danger' }
> = {
  pending: { label: 'ждёт', tone: 'warning' },
  succeeded: { label: 'прошёл', tone: 'success' },
  canceled: { label: 'не прошёл', tone: 'neutral' },
  refunded: { label: 'возвращён', tone: 'danger' },
};

/** Ось вариантов в редакторе: значения — строкой через запятую. */
export interface AxisDraft {
  name: string;
  values: string;
}

/** Строка варианта в редакторе: числа — строками, как в полях ввода. */
export interface VariantDraft {
  id?: string;
  options: Record<string, string>;
  sku: string;
  price: string;
  /** Зачёркнутая цена варианта; пусто — как у товара (9.11). */
  oldPrice: string;
  quantity: string;
  /** Сколько почт ждут поступления — только для показа (9.14). */
  waiting?: number;
}

/** Значения оси из строки «S, M, L»: без пустых и повторов. */
export function axisValues(raw: string): string[] {
  const out: string[] = [];

  for (const part of raw.split(',')) {
    const value = part.trim();

    if (value !== '' && !out.includes(value)) {
      out.push(value);
    }
  }

  return out;
}

/** «M / Чёрный» — значения в порядке осей. */
export function variantLabel(axes: { name: string }[], options: Record<string, string>): string {
  return axes
    .map((axis) => options[axis.name])
    .filter((value): value is string => value !== undefined)
    .join(' / ');
}

/**
 * Все сочетания значений осей. Уже заведённые варианты с теми же значениями
 * остаются как были — с артикулом, ценой и остатком; лишние уходят.
 */
export function combine(axes: AxisDraft[], current: VariantDraft[]): VariantDraft[] {
  const parsed = axes
    .map((axis) => ({ name: axis.name.trim(), values: axisValues(axis.values) }))
    .filter((axis) => axis.name !== '' && axis.values.length > 0);

  if (parsed.length === 0) {
    return [];
  }

  let combos: Record<string, string>[] = [{}];

  for (const axis of parsed) {
    combos = combos.flatMap((combo) =>
      axis.values.map((value) => ({ ...combo, [axis.name]: value })),
    );
  }

  const key = (options: Record<string, string>) =>
    parsed.map((axis) => options[axis.name] ?? '').join('\u0000');
  const existing = new Map(current.map((v) => [key(v.options), v]));

  return combos.map(
    (options) =>
      existing.get(key(options)) ?? { options, sku: '', price: '', oldPrice: '', quantity: '' },
  );
}

/** «10%» или «500 ₽». */
export function promoValue(p: Pick<PromoBody, 'kind' | 'value'>): string {
  return p.kind === 'percent' ? `${String(p.value)}%` : money(p.value);
}

/** Почему промокод сейчас не действует; undefined — действует. */
export function promoState(
  p: Pick<PromoBody, 'enabled' | 'starts_at' | 'ends_at' | 'usage_limit' | 'used_count'>,
  now = new Date(),
): { label: string; tone: 'neutral' | 'warning' | 'danger' } | undefined {
  if (!p.enabled) {
    return { label: 'выключен', tone: 'neutral' };
  }

  if (p.ends_at !== undefined && new Date(p.ends_at) < now) {
    return { label: 'истёк', tone: 'danger' };
  }

  if (p.starts_at !== undefined && new Date(p.starts_at) > now) {
    return { label: 'ещё не начался', tone: 'warning' };
  }

  if (p.usage_limit !== undefined && p.used_count >= p.usage_limit) {
    return { label: 'исчерпан', tone: 'danger' };
  }

  return undefined;
}

export const DELIVERY_KIND: Record<DeliveryMethodBody['kind'], string> = {
  courier: 'Курьер',
  pickup_point: 'Пункт выдачи',
  store: 'Самовывоз',
};

/** «300 ₽, бесплатно от 3 000 ₽» или «бесплатно». */
export function deliveryPrice(m: Pick<DeliveryMethodBody, 'price' | 'free_from'>): string {
  const base = m.price === 0 ? 'бесплатно' : money(m.price);

  return m.free_from === undefined || m.price === 0
    ? base
    : `${base}, бесплатно от ${money(m.free_from)}`;
}

/** Непустые строки без повторов: города через запятую, адреса по строке. */
export function linesOf(raw: string, separator: string): string[] {
  const out: string[] = [];

  for (const part of raw.split(separator)) {
    const value = part.trim();

    if (value !== '' && !out.some((v) => v.toLowerCase() === value.toLowerCase())) {
      out.push(value);
    }
  }

  return out;
}

/** Способы оплаты магазина — в порядке, в каком их предлагают в настройках. */
export const PAYMENT_METHODS = [
  { id: 'online', label: 'Онлайн', hint: 'тестовая страница на Development, ЮKassa на Production' },
  {
    id: 'on_delivery',
    label: 'При получении',
    hint: 'наличными или картой курьеру, в пункте выдачи',
  },
  {
    id: 'installments',
    label: 'Частями',
    hint: '4 платежа; на тесте — тестовая страница с графиком',
  },
] as const;

export const PAYMENT_METHOD_LABEL: Record<string, string> = Object.fromEntries(
  PAYMENT_METHODS.map((m) => [m.id, m.label]),
);

/** Почему сертификатом нельзя заплатить; undefined — можно. */
export function giftState(
  card: Pick<GiftCardBody, 'disabled' | 'balance' | 'expires_at'>,
  now = new Date(),
): { label: string; tone: 'neutral' | 'danger' } | undefined {
  if (card.disabled) {
    return { label: 'отключён', tone: 'neutral' };
  }

  if (card.expires_at !== undefined && new Date(card.expires_at) <= now) {
    return { label: 'истёк', tone: 'danger' };
  }

  if (card.balance <= 0) {
    return { label: 'потрачен', tone: 'neutral' };
  }

  return undefined;
}

/** Возврат (шаг 9.14) — словами и цветом. */
export const RETURN_STATUS: Record<
  ReturnBodyStatus,
  { label: string; tone: 'warning' | 'success' | 'neutral' | 'danger' }
> = {
  requested: { label: 'ждёт решения', tone: 'warning' },
  refunded: { label: 'деньги вернули', tone: 'success' },
  rejected: { label: 'отказ', tone: 'neutral' },
};

/** Сумма позиций по ценам заказа — подсказка, сколько вернуть. */
export function returnSum(items: { price?: number; qty: number }[]): number {
  return Math.round(items.reduce((sum, it) => sum + (it.price ?? 0) * it.qty, 0) * 100) / 100;
}

/** Позиции возврата одной строкой: «Кружка × 2, Футболка (M) × 1». */
export function returnItemsLabel(
  items: { title?: string; options?: string; qty: number }[],
): string {
  if (items.length === 0) {
    return 'без товара';
  }

  return items
    .map((it) => {
      const name = it.title === undefined || it.title === '' ? 'позиция' : it.title;

      return `${name}${it.options ? ` (${it.options})` : ''} × ${String(it.qty)}`;
    })
    .join(', ');
}
